import { assign, createMachine } from "xstate";
import {
  createOrchestrator,
  ITvOrchestrator,
} from "@nju/data-access-portal-orchestrator";
import { BasicError } from "@nju/result";
import { generatePepper, hashWithSHA384 } from "./utils";

interface IContext {
  tvId: string;
  code: string;
  userId: string | undefined;
  pepperInput: string;
  codeError?: string;
}

type IEvent =
  | { type: "SET_CODE"; value: string }
  | { type: "SET_CODE_SUCCESS"; orchestrator: ITvOrchestrator }
  | { type: "ERROR" }
  | { type: "SET_API_ERROR_CODE" }
  | { type: "SUBMIT" };

export const tvRegistrationMachine = createMachine(
  {
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    initial: "ready",
    states: {
      ready: {
        on: {
          SET_CODE: {
            actions: "setCode",
          },
          SUBMIT: {
            target: "validate",
          },
        },
      },
      validate: {
        entry: "validateForm",
        always: [
          {
            cond: "hasNoCodeErrors",
            target: "setConfidentialCode",
          },
          {
            target: "ready",
          },
        ],
      },
      setConfidentialCode: {
        invoke: {
          src: "setConfidentialCode",
        },
        on: {
          SET_CODE_SUCCESS: {
            target: "setCodeSuccess",
          },
          SET_API_ERROR_CODE: {
            actions: "setApiErrorCode",
            target: "ready",
          },
          ERROR: {
            target: "error",
          },
        },
      },
      setCodeSuccess: {
        type: "final",
      },
      error: {
        type: "final",
      },
      final: {
        type: "final",
      },
    },
  },
  {
    guards: {
      hasNoCodeErrors: ({ codeError }) => {
        return codeError === undefined;
      },
    },
    actions: {
      validateForm: assign<IContext, IEvent>({
        codeError: ({ code }) => {
          if (!code) {
            return "Aby przejść dalej, wpisz kod poufny.";
          }

          if (!/^\d{4}$/.test(code)) {
            return "Kod poufny musi się składać z czterech cyfr.";
          }

          if (!/^(?<!\d)(?!0{4})\d{4}(?!\d)/.test(code)) {
            return "Kod poufny nie może składać się z czterech zer.";
          }

          return;
        },
      }),
      setCode: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_CODE") {
          return {
            code: event.value,
          };
        }

        return context;
      }),
      setApiErrorCode: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_API_ERROR_CODE") {
          return {
            codeError: "Nowy kod poufny musi różnić się od poprzedniego.",
          };
        }

        return context;
      }),
    },
    services: {
      setConfidentialCode: ({ tvId, code, userId, pepperInput }) => {
        return async (callback) => {
          try {
            if (!tvId || !code) {
              throw new Error("Missing television id or pin code");
            }

            if (!pepperInput) {
              throw new Error("Missing PepperInput");
            }

            const hashedCode = hashWithSHA384(code);
            const pepper = generatePepper(pepperInput);
            const hashedPassAndPepper = hashWithSHA384(hashedCode + pepper); // hashing code + pepper

            const result = (
              await createOrchestrator("NJU_TV_SERVICE", {
                userId,
                product: {
                  id: tvId,
                  characteristic: { tvPin: hashedPassAndPepper },
                },
              })
            ).unwrap();

            return callback({
              type: "SET_CODE_SUCCESS",
              orchestrator: result,
            });
          } catch (error) {
            if (error instanceof BasicError && error.code === "PREVIOUS_CODE") {
              return callback({
                type: "SET_API_ERROR_CODE",
              });
            }

            return callback({ type: "ERROR" });
          }
        };
      },
    },
  },
);
